import React, { useState, useEffect } from 'react';
import '../styles/Authors.css';

const ReviewProcess = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
  return (
    <>
      {/* Hero Section */}
      <div className="hero-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto text-center">
              <h1 className="hero-title">
                <i className="fas fa-search me-3"></i>
                Peer Review Process
              </h1>
              <p className="hero-subtitle">
                Rigorous, transparent, and fair evaluation for quality scholarly publishing
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-4">
        <div className="row mb-5">
          <div className="col-lg-10 mx-auto">
          
          <div className="content-card" style={{ animationDelay: '0.1s' }}>
            <div className="content-card-header">
              <h2 className="content-card-title">
                <i className="fas fa-award me-3 text-warning"></i>
                Our Commitment to Quality
              </h2>
            </div>
              <p className="card-text">
                FUWJALS maintains the highest standards of scholarly publishing through a rigorous, 
                transparent, and fair peer review process. Our double-blind review system ensures 
                objective evaluation of all manuscripts while protecting the anonymity of both 
                authors and reviewers.
              </p>
            </div>
          </div>
          
          <div className="content-card" style={{ animationDelay: '0.2s' }}>
            <div className="content-card-header">
              <h2 className="content-card-title">
                <i className="fas fa-tasks me-3 text-primary"></i>
                Review Process Overview
              </h2>
            </div>
            <div className="row">
              <div className="col-md-12">
                <div className="timeline-container">
                  <div className="timeline-step mb-4 animate-on-scroll" id="step1"
                       style={{ animation: isVisible.step1 ? 'slideInLeft 0.6s ease-out' : 'none' }}>
                    <div className="timeline-badge bg-primary">1</div>
                    <div className="timeline-content"
                         onMouseEnter={() => setActiveStep(1)}
                         style={{
                           transform: activeStep === 1 ? 'scale(1.02)' : 'scale(1)',
                           boxShadow: activeStep === 1 ? '0 8px 25px rgba(0,0,0,0.1)' : ''
                         }}>
                      <h4>Initial Submission</h4>
                      <p>Authors submit manuscripts through our online system with all required documents.</p>
                      <small className="text-muted">Timeline: Immediate confirmation</small>
                    </div>
                  </div>
                    
                    <div className="timeline-step mb-4">
                      <div className="d-flex align-items-start">
                        <div className="timeline-badge bg-info">2</div>
                        <div className="timeline-content">
                          <h4>Editorial Screening</h4>
                          <p>Editor-in-Chief conducts initial review for scope, quality, and completeness.</p>
                          <small className="text-muted">Timeline: 5-7 business days</small>
                        </div>
                      </div>
                    </div>
                    
                    <div className="timeline-step mb-4">
                      <div className="d-flex align-items-start">
                        <div className="timeline-badge bg-warning">3</div>
                        <div className="timeline-content">
                          <h4>Section Editor Assignment</h4>
                          <p>Manuscripts are assigned to appropriate Section Editors based on expertise area.</p>
                          <small className="text-muted">Timeline: 2-3 business days</small>
                        </div>
                      </div>
                    </div>
                    
                    <div className="timeline-step mb-4">
                      <div className="d-flex align-items-start">
                        <div className="timeline-badge bg-success">4</div>
                        <div className="timeline-content">
                          <h4>Reviewer Selection</h4>
                          <p>Section Editors identify and invite qualified peer reviewers.</p>
                          <small className="text-muted">Timeline: 1-2 weeks</small>
                        </div>
                      </div>
                    </div>
                    
                    <div className="timeline-step mb-4">
                      <div className="d-flex align-items-start">
                        <div className="timeline-badge bg-danger">5</div>
                        <div className="timeline-content">
                          <h4>Peer Review</h4>
                          <p>Independent reviewers evaluate manuscripts and provide detailed feedback.</p>
                          <small className="text-muted">Timeline: 4-6 weeks</small>
                        </div>
                      </div>
                    </div>
                    
                    <div className="timeline-step mb-4">
                      <div className="d-flex align-items-start">
                        <div className="timeline-badge bg-secondary">6</div>
                        <div className="timeline-content">
                          <h4>Editorial Decision</h4>
                          <p>Editor-in-Chief makes final decision based on reviewer recommendations.</p>
                          <small className="text-muted">Timeline: 1-2 weeks</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="content-card" style={{ animationDelay: '0.3s' }}>
            <div className="content-card-header">
              <h2 className="content-card-title">
                <i className="fas fa-user-secret me-3 text-info"></i>
                Double-Blind Review System
              </h2>
            </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="card border-primary h-100">
                    <div className="card-header bg-primary text-white">
                      <h4><i className="fas fa-user-secret me-2"></i>Author Anonymity</h4>
                    </div>
                    <div className="card-body">
                      <ul>
                        <li>Author names and affiliations removed</li>
                        <li>Identifying references anonymized</li>
                        <li>Acknowledgments section hidden</li>
                        <li>Self-citations handled carefully</li>
                        <li>Institutional details masked</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="card border-success h-100">
                    <div className="card-header bg-success text-white">
                      <h4><i className="fas fa-eye-slash me-2"></i>Reviewer Anonymity</h4>
                    </div>
                    <div className="card-body">
                      <ul>
                        <li>Reviewer identities protected</li>
                        <li>Anonymous review reports</li>
                        <li>No direct author-reviewer contact</li>
                        <li>Confidential review process</li>
                        <li>Secure communication channels</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Review Criteria</h2>
              <div className="row">
                <div className="col-md-6">
                  <h4>Scientific Merit</h4>
                  <ul>
                    <li><strong>Originality:</strong> Novel contribution to the field</li>
                    <li><strong>Significance:</strong> Importance of research findings</li>
                    <li><strong>Validity:</strong> Sound methodology and analysis</li>
                    <li><strong>Reproducibility:</strong> Clear and replicable methods</li>
                    <li><strong>Ethics:</strong> Compliance with ethical standards</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h4>Technical Quality</h4>
                  <ul>
                    <li><strong>Methodology:</strong> Appropriate research design</li>
                    <li><strong>Statistics:</strong> Correct statistical analyses</li>
                    <li><strong>Data Quality:</strong> Adequate sample size and controls</li>
                    <li><strong>Literature:</strong> Comprehensive review of relevant work</li>
                    <li><strong>Conclusions:</strong> Well-supported interpretations</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h4>Presentation Quality</h4>
                  <ul>
                    <li><strong>Organization:</strong> Logical structure and flow</li>
                    <li><strong>Clarity:</strong> Clear and concise writing</li>
                    <li><strong>Language:</strong> Proper English usage</li>
                    <li><strong>Figures/Tables:</strong> Appropriate and well-designed</li>
                    <li><strong>References:</strong> Complete and accurate citations</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h4>Scope Alignment</h4>
                  <ul>
                    <li><strong>Journal Fit:</strong> Alignment with FUWJALS scope</li>
                    <li><strong>Target Audience:</strong> Relevance to readership</li>
                    <li><strong>Regional Focus:</strong> Application to local contexts</li>
                    <li><strong>Practical Impact:</strong> Potential for real-world application</li>
                    <li><strong>Innovation:</strong> Advancement of agricultural sciences</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Decision Categories</h2>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="card border-success">
                    <div className="card-header bg-success text-white">
                      <h5><i className="fas fa-check-circle me-2"></i>Accept</h5>
                    </div>
                    <div className="card-body">
                      <p>Manuscript meets all standards with no major revisions needed.</p>
                      <small>Next step: Publication processing</small>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6 mb-3">
                  <div className="card border-primary">
                    <div className="card-header bg-primary text-white">
                      <h5><i className="fas fa-edit me-2"></i>Minor Revision</h5>
                    </div>
                    <div className="card-body">
                      <p>Generally acceptable with small improvements needed.</p>
                      <small>Timeline: 2-4 weeks for revision</small>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6 mb-3">
                  <div className="card border-warning">
                    <div className="card-header bg-warning text-dark">
                      <h5><i className="fas fa-wrench me-2"></i>Major Revision</h5>
                    </div>
                    <div className="card-body">
                      <p>Substantial improvements required before acceptance.</p>
                      <small>Timeline: 4-8 weeks for revision</small>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6 mb-3">
                  <div className="card border-danger">
                    <div className="card-header bg-danger text-white">
                      <h5><i className="fas fa-times-circle me-2"></i>Reject</h5>
                    </div>
                    <div className="card-body">
                      <p>Manuscript does not meet publication standards.</p>
                      <small>Option: Submit to another journal</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Reviewer Selection</h2>
              <div className="row">
                <div className="col-md-6">
                  <h4>Qualification Criteria</h4>
                  <ul>
                    <li>Ph.D. or equivalent in relevant field</li>
                    <li>Active research in manuscript topic area</li>
                    <li>Recent publications in peer-reviewed journals</li>
                    <li>No conflicts of interest with authors</li>
                    <li>Available to complete review within deadline</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h4>Geographic Diversity</h4>
                  <ul>
                    <li>International reviewer network</li>
                    <li>Regional expertise representation</li>
                    <li>Institutional diversity preferred</li>
                    <li>Career stage balance maintained</li>
                    <li>Gender and demographic diversity</li>
                  </ul>
                </div>
              </div>
              
              <div className="alert alert-info mt-4">
                <h5><i className="fas fa-users me-2"></i>Reviewer Database</h5>
                <p className="mb-0">
                  FUWJALS maintains an extensive database of qualified reviewers from around 
                  the world, ensuring expert evaluation across all our subject areas.
                </p>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Review Timeline</h2>
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead className="table-primary">
                    <tr>
                      <th>Stage</th>
                      <th>Standard Timeline</th>
                      <th>Expedited Timeline*</th>
                      <th>Responsible Party</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Initial Screening</td>
                      <td>5-7 days</td>
                      <td>2-3 days</td>
                      <td>Editor-in-Chief</td>
                    </tr>
                    <tr>
                      <td>Reviewer Assignment</td>
                      <td>1-2 weeks</td>
                      <td>3-5 days</td>
                      <td>Section Editor</td>
                    </tr>
                    <tr>
                      <td>Peer Review</td>
                      <td>4-6 weeks</td>
                      <td>2-3 weeks</td>
                      <td>External Reviewers</td>
                    </tr>
                    <tr>
                      <td>Editorial Decision</td>
                      <td>1-2 weeks</td>
                      <td>3-5 days</td>
                      <td>Editor-in-Chief</td>
                    </tr>
                    <tr className="table-info">
                      <td><strong>Total Process</strong></td>
                      <td><strong>8-12 weeks</strong></td>
                      <td><strong>4-6 weeks</strong></td>
                      <td><strong>Editorial Team</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-muted">
                <small>* Expedited review available for urgent submissions (additional fee may apply)</small>
              </p>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Quality Assurance</h2>
              <div className="row">
                <div className="col-md-6">
                  <h4>Editorial Oversight</h4>
                  <ul>
                    <li>Editor-in-Chief final review authority</li>
                    <li>Section Editor expertise matching</li>
                    <li>Multiple reviewer requirement</li>
                    <li>Reviewer performance monitoring</li>
                    <li>Editorial board consultation</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h4>Technical Checks</h4>
                  <ul>
                    <li>Plagiarism detection software</li>
                    <li>Statistical review for data-heavy manuscripts</li>
                    <li>Reference verification</li>
                    <li>Ethical compliance screening</li>
                    <li>Format and style consistency</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Reviewer Recognition</h2>
              <div className="row">
                <div className="col-md-6">
                  <h4>Annual Recognition</h4>
                  <ul>
                    <li>Outstanding Reviewer Awards</li>
                    <li>Public acknowledgment on website</li>
                    <li>Certificate of recognition</li>
                    <li>Priority invitation to special issues</li>
                    <li>Editorial board consideration</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h4>Continuous Benefits</h4>
                  <ul>
                    <li>Free access to all journal content</li>
                    <li>Early access to accepted manuscripts</li>
                    <li>Publication fee discounts</li>
                    <li>Professional development opportunities</li>
                    <li>Networking with editorial board</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Appeals Process</h2>
              <p className="card-text">
                Authors who believe their manuscript was unfairly rejected may submit an appeal. 
                The appeal process ensures fairness and provides an opportunity for reconsideration.
              </p>
              
              <h4>Appeal Procedure</h4>
              <ol>
                <li><strong>Written Appeal:</strong> Submit detailed appeal letter within 30 days</li>
                <li><strong>Evidence Submission:</strong> Provide supporting documentation</li>
                <li><strong>Independent Review:</strong> Alternative reviewers evaluate the case</li>
                <li><strong>Editorial Committee:</strong> Final decision by independent editors</li>
                <li><strong>Response:</strong> Decision communicated within 4-6 weeks</li>
              </ol>
              
              <div className="alert alert-warning">
                <h5><i className="fas fa-gavel me-2"></i>Appeal Outcomes</h5>
                <ul className="mb-0">
                  <li><strong>Upheld:</strong> Original decision confirmed</li>
                  <li><strong>Reversed:</strong> Manuscript sent for new review</li>
                  <li><strong>Modified:</strong> Revised decision category assigned</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Becoming a Reviewer</h2>
              <p className="card-text">
                FUWJALS welcomes qualified researchers to join our reviewer network and 
                contribute to maintaining high publication standards in agricultural and life sciences.
              </p>
              
              <div className="row">
                <div className="col-md-6">
                  <h4>Requirements</h4>
                  <ul>
                    <li>Ph.D. in relevant field</li>
                    <li>Active research experience</li>
                    <li>Publication track record</li>
                    <li>Commitment to timely reviews</li>
                    <li>Professional integrity</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h4>Application Process</h4>
                  <ul>
                    <li>Complete online reviewer profile</li>
                    <li>Specify expertise areas</li>
                    <li>Provide CV and publication list</li>
                    <li>Submit professional references</li>
                    <li>Agree to reviewer guidelines</li>
                  </ul>
                </div>
              </div>
              
              <div className="text-center mt-4">
                <a href="/become-reviewer" className="btn btn-primary">
                  <i className="fas fa-user-plus me-2"></i>Join Our Reviewer Network
                </a>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Contact Information</h2>
              <div className="row">
                <div className="col-md-6">
                  <h5>Editorial Office</h5>
                  <p>
                    <i className="fas fa-envelope me-2"></i>editorial@fuwukari.edu.ng<br />
                    <i className="fas fa-phone me-2"></i>+234 XXX XXX XXXX<br />
                    <i className="fas fa-clock me-2"></i>Mon-Fri, 9 AM - 5 PM WAT
                  </p>
                </div>
                <div className="col-md-6">
                  <h5>Reviewer Coordination</h5>
                  <p>
                    <i className="fas fa-envelope me-2"></i>reviewers@fuwukari.edu.ng<br />
                    <i className="fas fa-question-circle me-2"></i>Review status inquiries<br />
                    <i className="fas fa-user-check me-2"></i>Reviewer applications
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Call to Action */}
          <div className="text-center mt-5">
            <a href="/submit-paper" className="btn btn-primary btn-lg me-3" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '50px',
              padding: '0.75rem 2rem',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
              transition: 'all 0.3s ease'
            }}>
              <i className="fas fa-upload me-2"></i>
              Submit Your Manuscript
            </a>
            <a href="/become-reviewer" className="btn btn-outline-primary btn-lg" style={{
              borderRadius: '50px',
              padding: '0.75rem 2rem',
              borderWidth: '2px'
            }}>
              <i className="fas fa-user-plus me-2"></i>
              Become a Reviewer
            </a>
          </div>
    </>
  );
};

export default ReviewProcess;